import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import type { AtLeast } from '#layers/base/public';
import type { RelatedItemState } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import type { AnyItem, CompositePrimaryKey, FieldSelection, Filter, GlobalFieldId, IdFieldSelection, M2MStructure, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { CreateItem, RelationFormFieldContext } from '#layers/forms/public';
import { pickNonSpecialProps } from '@rstore/shared';
import { cloneDeep, pickBy } from 'lodash-es';
import { computed, ref, toValue, watch } from 'vue';
import { isNullishOrEmpty } from '#layers/base/public';
import { useDisplayTemplateFieldSelection } from '#layers/blocks/composables/use-display-template-field-selection';
import { useFieldSelectionContexts } from '#layers/blocks/composables/use-field-selection-contexts';
import { paginateRelationPageItems } from '#layers/blocks/utils/relation/paginate-relation-page-items';
import { useItems } from '#layers/collection-query/public';
import { buildFilterFromItemKeys, getCollectionPrimaryKeyFields, getItemPrimaryKey, getM2MStructure, getRelationReferenceKeyFields, idFieldSelectionToFieldSelection, isConnectToOne, isCreateToMany, isCreateToOne, isDeleteToMany, isSameKey, isUpdateToMany, mergeFieldSelections, pickByPrimitiveFields, resolveFieldDisplayFieldSelection, stringifyStableId } from '#layers/data-model/public';
import { getOriginalItemKey, injectFormBlockContext, isConnectedAsCreated, isCreatedItem, markConnectedAsCreated } from '#layers/forms/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';

type RequiredFields = 'field' | 'referencedItemKey' | 'fieldValue';

/**
 * M2M uses _create/_delete operations on junction items (NOT _connect/_disconnect like O2M).
 *
 * Operations mapping:
 * - Connect existing target → emitCreate (junction item with both FKs)
 * - Disconnect target → emitDelete (junction item key)
 * - Create new target → emitCreate (junction with nested target _create)
 * - Update target → emitUpdate (junction filter with nested target _update)
 */
export interface UseM2MStateOptions extends AtLeast<RelationFormFieldContext<'to-many'>, RequiredFields> {
	pageSize?: MaybeRefOrGetter<number | undefined>;
	/**
	 * Extra target-field api names to fetch beyond the ones implied by the target display template
	 * and the source display block. Lets a caller (e.g. the user picker) render a richer connected
	 * row (avatar + email) than the target display template alone selects, without changing the
	 * display template. Defaults to none, so existing callers are unaffected.
	 */
	additionalSelection?: FieldSelection;
}

export interface ConnectTargetOptions {
	/** Flag the connected target to display as "created" rather than "connected". */
	markAsCreated?: boolean;
	/**
	 * Target row data already on hand at connect time (e.g. the picker row the user clicked).
	 * A `_connect` junction only carries the target key, so the display would otherwise render a
	 * bare placeholder until a server re-fetch of the target resolves. Seeding it here lets the
	 * connected row render its name/avatar immediately; the fetched copy overrides it once it lands.
	 */
	targetData?: AnyItem;
}

export interface M2MStateHandlers {
	connectTarget: (targetKey: CompositePrimaryKey, options?: ConnectTargetOptions) => void;
	disconnectTarget: (targetKey: CompositePrimaryKey) => void;
	createTarget: (data: AnyItem) => void;
	undoCreateTarget: (item: AnyItem) => void;
	updateTarget: (target: CompositePrimaryKey | null, data: AnyItem) => void;
	/**
	 * Record a pending edit to a newly connected/uploaded (already-existing) target.
	 * The junction stays a `_connect`; the edit is persisted via {@link captureTargetEdits}.
	 */
	editConnectedTarget: (targetKey: CompositePrimaryKey, data: AnyItem) => void;
	/** Capture pending connected-target edits for persistence directly to the target collection. */
	captureTargetEdits: () => () => Promise<void>;
}

export interface UseM2MStateReturn extends M2MStateHandlers {
	/** Junction items with embedded target data */
	items: ComputedRef<AnyItem[]>;
	/** Target collection (for display template and drawers) */
	targetCollection: ComputedRef<ResolvedMonospaceCollection | undefined>;
	/** Junction collection */
	junctionCollection: ComputedRef<ResolvedMonospaceCollection | undefined>;
	/** Full M2M structure for advanced use */
	m2mStructure: ComputedRef<M2MStructure | null>;
	/**
	 * Filter matching all connected targets (for exclusion in selection drawer).
	 * Usage: pass directly as filter prop (already includes _not).
	 */
	connectedTargetsFilter: ComputedRef<Filter>;
	/** Target keys of junctions pending deletion; DB-connected rows the picker re-includes */
	pendingDeleteTargetKeys: ComputedRef<CompositePrimaryKey[]>;
	isTargetPendingCreate: (targetKey: CompositePrimaryKey) => boolean;
	/** Get junction item's composite key */
	getJunctionKey: (item: AnyItem) => CompositePrimaryKey | null;
	/** Extract embedded target from junction item */
	getTargetFromJunction: (junctionItem: AnyItem) => AnyItem | null;
	/** Get target's primary key from junction item */
	getTargetKeyFromJunction: (junctionItem: AnyItem) => CompositePrimaryKey | null;
	/** Get the key stored by the junction's target FK */
	getTargetReferenceKeyFromJunction: (junctionItem: AnyItem) => CompositePrimaryKey | null;
	/** Current page number (1-indexed) */
	pageNumber: Ref<number>;
	/** Total items count including pending creates */
	totalItemsCount: ComputedRef<number | undefined>;
	/** Current items count (loaded + pending creates) */
	currentItemsCount: ComputedRef<number>;
	/** Pending creates included in the counts above, which belong to no server page */
	unpagedItemsCount: ComputedRef<number>;
	/** Whether data is being fetched */
	isLoading: ComputedRef<boolean>;
	/** State detection: junction creates a brand-new target (nested _create), not a connect to an existing one */
	isNewTargetCreate: (item: AnyItem) => boolean;
	/** State detection: junction is newly created with nested _create for target */
	isNewlyCreated: (item: AnyItem) => boolean;
	/** State detection: junction is newly created with nested _connect for existing target */
	isNewlyConnected: (item: AnyItem) => boolean;
	/** State detection: junction is pending deletion */
	isNewlyDisconnected: (item: AnyItem) => boolean;
	/** State detection: junction has pending update */
	isNewlyUpdated: (item: AnyItem) => boolean;
	/** Classify a junction row into its display state/badge, derived from the state predicates above */
	getItemState: (item: AnyItem) => RelatedItemState;
	/** Primary key fields for the junction collection */
	junctionPrimaryKeyFields: ComputedRef<ReturnType<typeof getCollectionPrimaryKeyFields> | null>;
	/** Fields a target is connected/matched by: the relation's referenced key, falling back to pk */
	targetReferenceKeyFields: ComputedRef<ResolvedMonospacePrimitiveField[] | null>;
}

/**
 * Recursively locate the nested selection stored under a given relation field id.
 * The source field's display block nests target fields under the junction's
 * toTarget relation, e.g. `{ include: { m2mFieldId: { include: { toTargetId: { fields: [...] } } } } }`.
 */
export function findNestedSelection(selection: IdFieldSelection, fieldId: GlobalFieldId): IdFieldSelection | null {
	const entries = Object.entries(selection.include ?? {});

	for (const [key, value] of entries) {
		const nested = value;

		if (key === fieldId) {
			// The target selection is reused in independent reads; paging and filters belong to the source include.
			return {
				...(nested.fields !== undefined ? { fields: nested.fields } : {}),
				...(nested.include !== undefined ? { include: nested.include } : {}),
			};
		}

		if (!nested) continue;

		const found = findNestedSelection(nested, fieldId);
		if (found) return found;
	}

	return null;
}

/**
 * Own `$`-prefixed keys that hold real data — studio-only virtual display fields such
 * as the file `$thumbnail` self-relation. `pickNonSpecialProps` strips every `$`-key to
 * drop rstore's reactive specials (which are non-enumerable proxy getters), but those
 * studio fields are genuine fetched data the display needs, so preserve them separately.
 */
function pickVirtualDisplayFields(item: AnyItem | null | undefined): AnyItem {
	return pickBy(item ?? {}, (_value, key) => key.startsWith('$'));
}

export function useM2MState(options: UseM2MStateOptions): UseM2MStateReturn {
	const { referencedItemKey, fieldValue, pageSize: pageSizeOption } = options;
	const { findCollectionById } = useWorkspaceSchemaStore();
	const store = useWorkspaceEntityStore();

	// A not-yet-persisted parent has no connected junctions in the database, and in create mode its
	// reference key is the live form value that changes on every keystroke. Querying on it would
	// refetch per keystroke — flashing the pagination controls.
	const formBlockContext = injectFormBlockContext(null);
	const isPersistedParent = computed(() => !(formBlockContext?.isCreate.value ?? false));

	const pageSize = computed(() => toValue(pageSizeOption) ?? 25);
	const pageNumber = ref(1);

	/**
	 * Edits to newly connected/uploaded targets, keyed by target primary key string.
	 * These targets already exist in the DB, so their field changes cannot ride inside
	 * the junction `_create` (the engine only accepts `_create`/`_connect` there). They
	 * are applied as direct updates to the target collection on save (see captureTargetEdits).
	 */
	const pendingTargetEdits = ref(new Map<string, {
		collectionApiName: string;
		key: CompositePrimaryKey;
		data: AnyItem;
	}>());

	/**
	 * Target row data seeded at connect time (see {@link ConnectTargetOptions.targetData}), keyed by
	 * target reference key string. Fills the display for a freshly connected `_connect` junction until
	 * {@link pendingTargetsMap} (a server re-fetch) has the row, avoiding a placeholder flicker.
	 */
	const seededConnectTargets = ref(new Map<string, AnyItem>());

	const computedField = computed(() => toValue(options.field));
	const sourceReferenceKey = computed(() => options.referencedItemReferenceKey === undefined
		? toValue(referencedItemKey)
		: toValue(options.referencedItemReferenceKey));
	const relationOwner = computed(() => {
		const sourceKey = toValue(referencedItemKey);
		return `${computedField.value?.id ?? ''}:${sourceKey ? stringifyStableId(sourceKey) : ''}`;
	});

	watch(relationOwner, () => {
		pendingTargetEdits.value = new Map();
		seededConnectTargets.value = new Map();
		pageNumber.value = 1;
	});

	const m2mStructure = computed<M2MStructure | null>(() => {
		const field = computedField.value;
		if (!field) return null;

		try {
			return getM2MStructure(field);
		}
		catch {
			return null;
		}
	});

	const junctionCollection = computed(() =>
		m2mStructure.value ? findCollectionById(m2mStructure.value.junction.collectionId) : undefined,
	);

	const targetCollection = computed(() =>
		m2mStructure.value ? findCollectionById(m2mStructure.value.target.collectionId) : undefined,
	);

	const targetDisplayTemplateSelection = useDisplayTemplateFieldSelection(targetCollection);

	const { fieldSelectionCtx, idCtx } = useFieldSelectionContexts();

	/**
	 * The fields the related-item display actually needs for each target, derived from
	 * the source relation field's display block (e.g. file thumbnails need mediaType,
	 * title, etc.). The display template alone under-fetches these for pending items.
	 */
	const targetDisplayBlockSelection = computed<FieldSelection>(() => {
		const structure = m2mStructure.value;
		const sourceField = computedField.value;
		const sourceCollection = sourceField?.collection;

		if (!structure || !sourceField || !sourceCollection) return { fields: [] };

		try {
			const idSelection = resolveFieldDisplayFieldSelection(sourceField, sourceCollection, fieldSelectionCtx);
			const targetIdSelection = findNestedSelection(idSelection, structure.junction.toTarget.id);

			if (!targetIdSelection) return { fields: [] };

			return idFieldSelectionToFieldSelection(targetIdSelection, idCtx);
		}
		catch {
			return { fields: [] };
		}
	});

	const targetReferenceKeyFields = computed(() =>
		m2mStructure.value ? getRelationReferenceKeyFields(m2mStructure.value.junction.toTarget) : null,
	);

	const targetFieldSelection = computed<FieldSelection>(() =>
		mergeFieldSelections(
			targetDisplayTemplateSelection.value,
			targetDisplayBlockSelection.value,
			{ fields: targetReferenceKeyFields.value?.map((field) => field.apiName) ?? [] },
			options.additionalSelection ?? { fields: [] },
		),
	);

	const junctionPrimaryKeyFields = computed(() =>
		junctionCollection.value ? getCollectionPrimaryKeyFields(junctionCollection.value) : null,
	);
	const targetPrimaryKeyFields = computed(() =>
		targetCollection.value ? getCollectionPrimaryKeyFields(targetCollection.value) : null,
	);

	function targetReferenceKeyString(source: AnyItem): string {
		return stringifyStableId(pickByPrimitiveFields(source, targetReferenceKeyFields.value ?? []));
	}

	function getJunctionKey(item: AnyItem): CompositePrimaryKey | null {
		return getOriginalItemKey(item)
			?? (junctionPrimaryKeyFields.value
				? pickByPrimitiveFields(item, junctionPrimaryKeyFields.value)
				: null);
	}

	function getTargetFromJunction(junctionItem: AnyItem): AnyItem | null {
		if (!m2mStructure.value) return null;
		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const targetRelation = junctionItem[toTargetFieldName];

		// Handle _connect structure (pending creates for existing targets)
		if (isConnectToOne(targetRelation)) {
			// For _connect, we don't have the full target data, just the key
			// Return the key as partial target data for display purposes
			return targetRelation._connect.key ?? null;
		}

		// Handle _create structure (pending creates for new targets)
		if (isCreateToOne(targetRelation)) {
			return targetRelation._create.data ?? null;
		}

		return targetRelation ?? null;
	}

	function getTargetKeyFromJunction(junctionItem: AnyItem): CompositePrimaryKey | null {
		const target = getTargetFromJunction(junctionItem);
		if (!target || !targetPrimaryKeyFields.value) return null;

		const primaryKey = pickByPrimitiveFields(target, targetPrimaryKeyFields.value);
		const hasCompletePrimaryKey = targetPrimaryKeyFields.value.length > 0
			&& targetPrimaryKeyFields.value.every((field) => primaryKey[field.apiName] !== undefined);
		return hasCompletePrimaryKey ? primaryKey : null;
	}

	function getTargetReferenceKeyFromJunction(junctionItem: AnyItem): CompositePrimaryKey | null {
		if (!m2mStructure.value) return null;

		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const targetRelation = junctionItem[toTargetFieldName];

		// Handle _connect structure (pending creates for existing targets)
		if (isConnectToOne(targetRelation)) {
			return targetRelation._connect.key ?? null;
		}

		const constraint = m2mStructure.value.junction.toTarget.constraint;

		if (constraint) {
			const referenceKey: CompositePrimaryKey = {};
			const hasCompleteReferenceKey = constraint.fieldMappings.length > 0
				&& constraint.fieldMappings.every(({ constrainedField, referencedField }) => {
					if (!constrainedField || !referencedField) return false;

					const value = junctionItem[constrainedField.apiName];
					if (value === undefined) return false;

					referenceKey[referencedField.apiName] = value;
					return true;
				});

			if (hasCompleteReferenceKey) return referenceKey;
		}

		// Handle regular target data (fetched items)
		const target = getTargetFromJunction(junctionItem);
		if (!target || !targetReferenceKeyFields.value) return null;
		return pickByPrimitiveFields(target, targetReferenceKeyFields.value);
	}

	const connectedJunctionsFilter = computed<Filter | undefined>(() => {
		const sourceKey = sourceReferenceKey.value;
		if (!sourceKey || !m2mStructure.value) return;

		const toSourceRelation = m2mStructure.value.junction.toSource;
		const constraint = toSourceRelation.constraint;
		if (!constraint) return;

		// Map source primary key to junction FK fields
		const filterParts: Record<string, { _eq: unknown }> = {};

		for (const mapping of constraint.fieldMappings) {
			const constrainedFieldName = mapping.constrainedField!.apiName;
			const referencedFieldName = mapping.referencedField!.apiName;
			const value = sourceKey[referencedFieldName];

			if (isNullishOrEmpty(value)) return;

			filterParts[constrainedFieldName] = { _eq: value };
		}

		return filterParts;
	});

	const remoteItems = useItems({
		collection: () => m2mStructure.value?.junction.collectionId,
		enabled: () => !!m2mStructure.value && !!connectedJunctionsFilter.value && isPersistedParent.value,
		query: () => ({
			filter: connectedJunctionsFilter.value,
			...m2mStructure.value
				? {
						fields: ['*'],
						include: {
							[m2mStructure.value.junction.toTarget.apiName]: targetFieldSelection.value,
						},
					}
				: { fields: ['*'] },
		}),
		pageSize,
	});

	watch(remoteItems.pageNumber, (val) => {
		pageNumber.value = val;
	});

	watch(pageNumber, (val) => {
		remoteItems.pageNumber.value = val;
	});

	// Note: Unlike O2M which uses _disconnect operations, M2M uses _delete operations
	// on junction items. We do NOT filter out deleted items from display - they remain
	// visible with "disconnected" badges, matching O2M behavior.

	const pendingConnectTargetKeys = computed<CompositePrimaryKey[]>(() => {
		const ops = toValue(fieldValue);
		if (!ops || !m2mStructure.value) return [];

		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const keys: CompositePrimaryKey[] = [];

		for (const op of ops) {
			if (isCreateToMany(op)) {
				for (const junctionItem of op._create.data) {
					const targetRelation = junctionItem[toTargetFieldName];

					if (isConnectToOne(targetRelation)) {
						keys.push(targetRelation._connect.key);
					}
				}
			}
		}

		return keys;
	});

	const pendingTargetsFilter = computed<Filter>(() => {
		const keys = pendingConnectTargetKeys.value;

		if (keys.length === 0 || !targetReferenceKeyFields.value) {
			return {};
		}

		return buildFilterFromItemKeys({
			keys,
			primaryKeyFields: targetReferenceKeyFields.value,
		});
	});

	// Uses useItems for proper reactive fetching instead of cache-only
	const pendingTargetsFetch = useItems({
		collection: () => targetCollection.value,
		enabled: () => pendingConnectTargetKeys.value.length > 0 && !!targetCollection.value,
		query: () => ({
			filter: pendingTargetsFilter.value,
			...targetFieldSelection.value,
		}),
	});

	const pendingTargetsMap = computed<Map<string, AnyItem>>(() => {
		const map = new Map<string, AnyItem>();
		if (!targetReferenceKeyFields.value || !targetCollection.value) return map;

		for (const target of pendingTargetsFetch.items.value) {
			const keyString = targetReferenceKeyString(target);
			map.set(keyString, target);
		}

		return map;
	});

	const newlyDeletedJunctionKeys = computed<CompositePrimaryKey[]>(() => {
		const ops = toValue(fieldValue);
		if (!ops) return [];

		return ops
			.filter((op) => isDeleteToMany(op))
			.map((op) => op._delete.filter as CompositePrimaryKey);
	});

	const updateOps = computed(() => toValue(fieldValue)?.filter((op) => isUpdateToMany(op)) ?? []);

	const createdJunctionItems = computed(() => {
		const ops = toValue(fieldValue);
		if (!ops) return [];

		return ops
			.filter((op) => isCreateToMany(op))
			.flatMap((op) => op._create.data);
	});

	/**
	 * Extract actual data from nested relation operation for display.
	 * Handles array format: [{ _update: { filter: {...}, data: {...} } }] -> {...}
	 */
	function resolveNestedRelationForDisplay(
		item: AnyItem,
		updateData: AnyItem,
	): AnyItem {
		const toTargetFieldName = m2mStructure.value!.junction.toTarget.apiName;
		const nestedOps = updateData[toTargetFieldName];

		// If no nested operations for target relation, just merge as-is
		if (!Array.isArray(nestedOps) || nestedOps.length === 0) {
			return { ...pickNonSpecialProps(item), ...updateData };
		}

		// Get the existing target from the junction item, keeping its virtual display
		// fields (e.g. the file `$thumbnail`) that the edit never touches but
		// pickNonSpecialProps would otherwise drop.
		const rawTarget = item[toTargetFieldName];
		const existingTarget = { ...pickNonSpecialProps(rawTarget), ...pickVirtualDisplayFields(rawTarget) };
		const existingTargetKey = existingTarget && targetPrimaryKeyFields.value
			? pickByPrimitiveFields(existingTarget, targetPrimaryKeyFields.value)
			: null;

		// Find matching _update operation by filter (target key)
		const updateOp = nestedOps.find((op: AnyItem) => {
			if (!op?._update?.data) return false;

			// Match by filter if available, otherwise take first update
			if (op._update.filter && existingTargetKey) {
				return isSameKey(op._update.filter as CompositePrimaryKey, existingTargetKey);
			}

			return true;
		});

		if (updateOp?._update?.data) {
			const resolvedData = { ...updateData, [toTargetFieldName]: {
				...existingTarget,
				...updateOp._update.data,
			} };

			// Merge the actual target data with existing target data

			return { ...pickNonSpecialProps(item), ...resolvedData };
		}

		return { ...pickNonSpecialProps(item), ...updateData };
	}

	/**
	 * Check if a junction item matches an FK-based filter (source + target FKs).
	 * Used for matching update/delete filters which use FK fields, not PK.
	 */
	function junctionMatchesFKFilter(item: AnyItem, fkFilter: CompositePrimaryKey): boolean {
		if (!m2mStructure.value) return false;

		let matchedAny = false;

		// Check source FK fields
		const toSourceConstraint = m2mStructure.value.junction.toSource.constraint;

		if (toSourceConstraint) {
			for (const mapping of toSourceConstraint.fieldMappings) {
				const fkFieldName = mapping.constrainedField!.apiName;

				if (fkFilter[fkFieldName] !== undefined) {
					matchedAny = true;

					if (item[fkFieldName] !== fkFilter[fkFieldName]) {
						return false;
					}
				}
			}
		}

		// Check target FK fields
		const toTargetConstraint = m2mStructure.value.junction.toTarget.constraint;

		if (toTargetConstraint) {
			for (const mapping of toTargetConstraint.fieldMappings) {
				const fkFieldName = mapping.constrainedField!.apiName;

				if (fkFilter[fkFieldName] !== undefined) {
					matchedAny = true;

					if (item[fkFieldName] !== fkFilter[fkFieldName]) {
						return false;
					}
				}
			}
		}

		return matchedAny;
	}

	/**
	 * Apply pending _update operations to items for display.
	 */
	function applyUpdateEdits(items: AnyItem[]): AnyItem[] {
		const ops = toValue(fieldValue);
		if (!ops || ops.length === 0) return items;

		const pendingUpdateOps = ops.filter((op) => isUpdateToMany(op));
		if (pendingUpdateOps.length === 0) return items;

		return items.map((item) => {
			const itemKey = getJunctionKey(item);

			// Find matching update operation by either PK or FK-based filter
			const updateOp = pendingUpdateOps.find((op) => {
				const filter = op._update.filter;
				if (!filter || typeof filter !== 'object') return false;

				// Try PK match first (if itemKey exists)
				if (itemKey && isSameKey(filter as CompositePrimaryKey, itemKey)) {
					return true;
				}

				// Fall back to FK-based match (for filters built from target keys)
				return junctionMatchesFKFilter(item, filter as CompositePrimaryKey);
			});

			if (updateOp) {
				return resolveNestedRelationForDisplay(item, updateOp._update.data);
			}

			return item;
		});
	}

	/**
	 * Get enriched created junction items with cached target data.
	 * For _connect operations, enriches with full target data from cache.
	 */
	const enrichedCreatedItems = computed(() => {
		if (!m2mStructure.value) return [];

		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;

		return createdJunctionItems.value.map((junctionItem) => {
			const targetRelation = junctionItem[toTargetFieldName];

			// If this is a _connect operation, enrich with cached target data and
			// overlay any pending edit so the just-edited fields show before save.
			if (isConnectToOne(targetRelation) && targetCollection.value) {
				const targetKey = targetRelation._connect.key;
				const referenceKeyString = targetReferenceKeyString(targetKey);
				// Prefer the fetched row once it lands; fall back to the seed so the row renders immediately.
				const cachedTarget = pendingTargetsMap.value.get(referenceKeyString) ?? seededConnectTargets.value.get(referenceKeyString);
				// Pending edits are keyed by the target's primary key. Resolve it from the cached/seeded
				// row when present; otherwise fall back to the reference key (=PK)
				const pendingEditKey = cachedTarget
					? stringifyStableId(getItemPrimaryKey(cachedTarget, targetCollection.value))
					: referenceKeyString;
				const pendingEdit = pendingTargetEdits.value.get(pendingEditKey);

				if (cachedTarget || pendingEdit) {
					return {
						...junctionItem,
						[toTargetFieldName]: { ...(cachedTarget ?? targetKey), ...pendingEdit?.data },
					};
				}
			}

			return junctionItem;
		});
	});

	/**
	 * Compute the current page's display items:
	 * - Server-paginated remote junction rows for this page, with pending updates applied
	 * - Pending (unsaved) creates appended after ALL remote rows in the combined sequence, then
	 *   sliced to this page — client-only creates can't be interleaved into server pagination, so
	 *   they trail the remote rows and each page renders exactly the rows the footer counts for it
	 * - Disconnected rows stay visible (they get a "disconnected" badge via isNewlyDisconnected)
	 */
	const displayItems = computed(() => {
		// Get this page's remote items with update edits applied
		const remotePageItems = applyUpdateEdits([...remoteItems.items.value]);

		const creates = enrichedCreatedItems.value;
		if (creates.length === 0) return remotePageItems;

		return paginateRelationPageItems({
			remotePageItems,
			pendingCreates: creates,
			pageNumber: remoteItems.pageNumber.value,
			pageSize: pageSize.value,
			// Fall back to the current page's length when the server count is unknown; the picker case
			// that motivated this (no remote rows, many pending creates) then still slices correctly.
			remoteTotal: remoteItems.totalItemsCount.value ?? remotePageItems.length,
			getKey: (item) => {
				const key = getTargetKeyFromJunction(item) ?? getTargetReferenceKeyFromJunction(item);
				return key ? stringifyStableId(key) : null;
			},
		});
	});

	/**
	 * Build a junction item data object with FKs to both source and target.
	 */
	function buildJunctionItem(targetKey: CompositePrimaryKey): AnyItem | null {
		const sourceKey = sourceReferenceKey.value;
		if (!sourceKey || !m2mStructure.value) return null;

		const junctionItem: AnyItem = {};

		// Map source key to junction FK (toSource constraint)
		const toSourceConstraint = m2mStructure.value.junction.toSource.constraint;

		if (toSourceConstraint) {
			for (const mapping of toSourceConstraint.fieldMappings) {
				junctionItem[mapping.constrainedField!.apiName] = sourceKey[mapping.referencedField!.apiName];
			}
		}

		// Map target key to junction FK (toTarget constraint)
		const toTargetConstraint = m2mStructure.value.junction.toTarget.constraint;

		if (toTargetConstraint) {
			for (const mapping of toTargetConstraint.fieldMappings) {
				junctionItem[mapping.constrainedField!.apiName] = targetKey[mapping.referencedField!.apiName];
			}
		}

		return junctionItem;
	}

	/**
	 * Build a filter to identify a junction item by source + target keys.
	 */
	function buildJunctionFilter(targetKey: CompositePrimaryKey): CompositePrimaryKey | null {
		const junctionItem = buildJunctionItem(targetKey);
		if (!junctionItem) return null;

		// The junction filter is essentially the junction item's composite key
		return junctionItem as CompositePrimaryKey;
	}

	/**
	 * Check if a junction item in fieldValue matches the given target key.
	 * Handles both:
	 * - Fetched items with direct FK values
	 * - Pending creates with nested _connect structure
	 */
	function junctionMatchesTarget(junctionItem: AnyItem | null | undefined, targetKey: CompositePrimaryKey | null | undefined): boolean {
		if (!m2mStructure.value || !junctionItem || !targetKey) return false;

		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const targetRelation = junctionItem[toTargetFieldName];

		// Check for _connect structure (pending creates for existing targets)
		if (isConnectToOne(targetRelation)) {
			return isSameKey(targetRelation._connect.key, targetKey);
		}

		// Fallback to checking FK values directly (fetched items)
		const toTargetConstraint = m2mStructure.value.junction.toTarget.constraint;
		if (!toTargetConstraint) return false;

		for (const mapping of toTargetConstraint.fieldMappings) {
			const junctionFkValue = junctionItem[mapping.constrainedField!.apiName];
			const targetPkValue = targetKey[mapping.referencedField!.apiName];

			if (junctionFkValue !== targetPkValue) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Check if target has a pending create operation.
	 * DB-connected items are excluded server-side via connectedTargetsFilter,
	 * so only pending creates need client-side guarding.
	 */
	function isTargetPendingCreate(targetKey: CompositePrimaryKey): boolean {
		return !!findPendingCreate(targetKey);
	}

	/**
	 * Check if there's a pending delete operation for the given junction key.
	 */
	function hasPendingDelete(junctionKey: CompositePrimaryKey): boolean {
		return toValue(fieldValue)?.some((op) =>
			isDeleteToMany(op) && isSameKey(op._delete.filter as CompositePrimaryKey, junctionKey),
		) ?? false;
	}

	/**
	 * Find a pending create operation for the given target.
	 */
	function findPendingCreate(targetKey: CompositePrimaryKey): AnyItem | undefined {
		const ops = toValue(fieldValue);
		if (!ops) return undefined;

		for (const op of ops) {
			if (isCreateToMany(op)) {
				const match = op._create.data.find((item) => junctionMatchesTarget(item, targetKey));
				if (match) return match;
			}
		}

		return undefined;
	}

	function connectTarget(targetKey: CompositePrimaryKey, connectOptions?: ConnectTargetOptions) {
		if (!m2mStructure.value) return;

		// Prevent duplicate connections (pending creates only; DB connections
		// are excluded server-side by connectedTargetsFilter)
		if (isTargetPendingCreate(targetKey)) return;

		// Check if this target was previously deleted - if so, just undo the delete
		const junctionFilter = buildJunctionFilter(targetKey);
		const wasPendingDelete = junctionFilter && hasPendingDelete(junctionFilter);

		if (wasPendingDelete) {
			options.emitUndoDelete?.(junctionFilter);
			return;
		}

		// Seed the display with any target data on hand so the connected row renders its name/avatar
		// immediately, instead of a placeholder until the target re-fetch resolves.
		if (connectOptions?.targetData && targetCollection.value) {
			const keyString = targetReferenceKeyString(targetKey);
			const next = new Map(seededConnectTargets.value);
			next.set(keyString, connectOptions.targetData);
			seededConnectTargets.value = next;
		}

		// Build junction item with _connect for the target relation
		// Source FK is implicit when creating through the source O2M field
		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const junctionItem: AnyItem = {
			[toTargetFieldName]: {
				_connect: {
					key: targetKey,
				},
			},
		};

		options.emitCreate?.(connectOptions?.markAsCreated ? markConnectedAsCreated(junctionItem) : junctionItem);
	}

	function disconnectTarget(targetKey: CompositePrimaryKey) {
		if (!m2mStructure.value) return;

		// Check if this was a pending create - if so, undo it instead
		const pendingCreate = findPendingCreate(targetKey);

		if (pendingCreate) {
			options.emitUndoCreate?.(pendingCreate as CreateItem);
			return;
		}

		// Otherwise emit delete for existing junction
		const junctionFilter = buildJunctionFilter(targetKey);

		if (junctionFilter) {
			options.emitDelete?.(junctionFilter);
		}
	}

	function createTarget(data: AnyItem) {
		if (!m2mStructure.value) return;

		// Build junction item with _create for the target relation
		// Source FK is implicit when creating through the source O2M field
		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const junctionItem = {
			[toTargetFieldName]: {
				_create: { data },
			},
		};

		options.emitCreate?.(junctionItem);
	}

	function undoCreateTarget(item: AnyItem) {
		// Drop any pending edit for this target so a cancelled connect never updates it.
		const primaryKey = getTargetKeyFromJunction(item);
		const referenceKey = getTargetReferenceKeyFromJunction(item);

		if (primaryKey) {
			const keyString = stringifyStableId(primaryKey);

			if (pendingTargetEdits.value.has(keyString)) {
				const next = new Map(pendingTargetEdits.value);
				next.delete(keyString);
				pendingTargetEdits.value = next;
			}
		}

		if (referenceKey) {
			const keyString = targetReferenceKeyString(referenceKey);

			if (seededConnectTargets.value.has(keyString)) {
				const next = new Map(seededConnectTargets.value);
				next.delete(keyString);
				seededConnectTargets.value = next;
			}
		}

		options.emitUndoCreate?.(item as CreateItem);
	}

	function editConnectedTarget(targetKey: CompositePrimaryKey, data: AnyItem) {
		const collection = targetCollection.value;
		if (!collection) return;

		const keyString = stringifyStableId(targetKey);
		const next = new Map(pendingTargetEdits.value);
		next.set(keyString, { collectionApiName: collection.apiName, key: targetKey, data });
		pendingTargetEdits.value = next;
	}

	function captureTargetEdits() {
		const edits = [...pendingTargetEdits.value].map(([id, edit]) => ({ id, edit, submitted: cloneDeep(edit) }));

		return async () => {
			for (const { id, edit, submitted: { collectionApiName, key, data } } of edits) {
				// RStore derives the primary key from the payload, including composite keys.
				await store.$collection(collectionApiName).update({ ...key, ...data });
				if (pendingTargetEdits.value.get(id) === edit) pendingTargetEdits.value.delete(id);
			}
		};
	}

	function updateTarget(target: CompositePrimaryKey | null, data: AnyItem) {
		if (!m2mStructure.value || !target || !targetPrimaryKeyFields.value || !targetReferenceKeyFields.value) return;

		const junctionFilter = buildJunctionFilter(pickByPrimitiveFields(target, targetReferenceKeyFields.value));
		if (!junctionFilter) return;

		// Build update payload with nested target update
		// Use array format because RelationOperationToOneInUpdate = SingleToOneOperation[]
		// Include filter to identify which target item to update
		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const updatePayload = {
			[toTargetFieldName]: [{ _update: { filter: pickByPrimitiveFields(target, targetPrimaryKeyFields.value), data } }],
		};

		options.emitUpdate?.(junctionFilter, updatePayload);
	}

	/**
	 * Filter matching all connected targets.
	 * Uses relational filter to exclude targets already connected to current source.
	 * Usage: pass directly as filter prop (already includes _not).
	 */
	// Target keys of junctions pending deletion (disconnected in the UI but still in the DB).
	// Shared by connectedTargetsFilter, which re-includes them in the picker, and the user
	// picker, which uses them to tell which visible members still have a real DB junction.
	const pendingDeleteTargetKeys = computed<CompositePrimaryKey[]>(() => {
		const toTargetConstraint = m2mStructure.value?.junction.toTarget.constraint;
		if (!toTargetConstraint || !targetReferenceKeyFields.value) return [];

		return newlyDeletedJunctionKeys.value.map((deleteKey) => {
			const targetKey: CompositePrimaryKey = {};

			for (const mapping of toTargetConstraint.fieldMappings) {
				const junctionFkName = mapping.constrainedField!.apiName;
				const targetPkName = mapping.referencedField!.apiName;
				const fkValue = deleteKey[junctionFkName];
				if (fkValue === undefined) continue;
				targetKey[targetPkName] = fkValue;
			}

			return targetKey;
		});
	});

	const connectedTargetsFilter = computed<Filter>(() => {
		const sourceKey = sourceReferenceKey.value;

		if (!sourceKey || !m2mStructure.value) {
			return {};
		}

		// Build relational filter: exclude targets that have a junction pointing to this source
		// e.g., { _not: { PlaylistTrack: { PlaylistId: { _eq: 1 } } } }
		const targetToJunctionField = m2mStructure.value.target.field;
		const junctionToSourceField = m2mStructure.value.junction.toSource;
		const toSourceConstraint = junctionToSourceField.constraint;

		if (!toSourceConstraint) {
			return {};
		}

		// Build the junction filter part (matching source key)
		const junctionFilter: Record<string, { _eq: unknown }> = {};

		for (const mapping of toSourceConstraint.fieldMappings) {
			const constrainedFieldName = mapping.constrainedField!.apiName;
			const referencedFieldName = mapping.referencedField!.apiName;
			junctionFilter[constrainedFieldName] = { _eq: sourceKey[referencedFieldName] };
		}

		// Relational filter through target's O2M to junction
		const relationalExclude: Filter = {
			_not: {
				[targetToJunctionField.apiName]: junctionFilter,
			},
		};

		// Also exclude pending creates (not yet in DB)
		const pendingTargetKeys = createdJunctionItems.value
			.map((item) => getTargetReferenceKeyFromJunction(item))
			.filter((key): key is CompositePrimaryKey => key !== null);

		const conditions: Filter[] = [relationalExclude];

		// Exclude pending creates
		if (pendingTargetKeys.length > 0) {
			const pendingOrConditions = pendingTargetKeys.map((key) => {
				const cond: Record<string, { _eq: unknown }> = {};

				for (const field of targetReferenceKeyFields.value!) {
					cond[field.apiName] = { _eq: key[field.apiName] };
				}

				return cond;
			});

			conditions.push({ _not: { _or: pendingOrConditions } });
		}

		// Re-include pending deletes (override the relational exclude)
		if (pendingDeleteTargetKeys.value.length > 0) {
			const deleteOrConditions = pendingDeleteTargetKeys.value.map((key) => {
				const cond: Record<string, { _eq: unknown }> = {};

				for (const field of targetReferenceKeyFields.value!) {
					cond[field.apiName] = { _eq: key[field.apiName] };
				}

				return cond;
			});

			// Structure: { _or: [{ _and: [excludes] }, { _or: [pending delete targets] }] }
			return {
				_or: [
					{ _and: conditions },
					{ _or: deleteOrConditions },
				],
			};
		}

		if (conditions.length === 1) {
			return relationalExclude;
		}

		return { _and: conditions };
	});

	// Pagination counts
	const totalItemsCount = computed(() => {
		if (remoteItems.totalItemsCount.value != null) {
			return remoteItems.totalItemsCount.value + createdJunctionItems.value.length;
		}
	});

	const currentItemsCount = computed(() => remoteItems.currentItemsCount.value + createdJunctionItems.value.length);

	// Unsaved rows are rendered on page one and belong to no server page, so the pagination controls
	// have to subtract them back out of the counts above.
	const unpagedItemsCount = computed(() => createdJunctionItems.value.length);

	const isLoading = computed(() => remoteItems.isLoading.value);

	// State detection methods
	function isNewTargetCreate(item: AnyItem): boolean {
		if (!isCreatedItem(item) || !m2mStructure.value) return false;

		// A genuine new target carries a nested _create; connects/uploads carry a _connect.
		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		return isCreateToOne(item[toTargetFieldName]);
	}

	function isNewlyCreated(item: AnyItem): boolean {
		if (!isCreatedItem(item)) return false;

		// Check if it's a created junction with a nested _create for the target (new target)
		if (!m2mStructure.value) return false;
		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const targetRelation = item[toTargetFieldName];

		return isCreateToOne(targetRelation);
	}

	function isNewlyConnected(item: AnyItem): boolean {
		if (!isCreatedItem(item)) return false;

		// Check if it's a created junction connecting to an existing target (not creating a new target)
		// After enrichment, the _connect structure is replaced with actual target data,
		// so we check that it's NOT a _create operation (which would mean new target)
		if (!m2mStructure.value) return false;
		const toTargetFieldName = m2mStructure.value.junction.toTarget.apiName;
		const targetRelation = item[toTargetFieldName];

		// If it's still _connect structure (not enriched), it's a connect
		if (isConnectToOne(targetRelation)) return true;

		// If enriched (actual target data) and NOT a _create, it's a connect
		// A created item with plain target data must be a connect operation
		return targetRelation && !isCreateToOne(targetRelation);
	}

	function isNewlyDisconnected(item: AnyItem): boolean {
		const key = getJunctionKey(item);

		return newlyDeletedJunctionKeys.value.some((deletedKey) => {
			if (key && isSameKey(deletedKey, key)) return true;
			// Delete filter uses FK fields, fall back to FK-based match
			return junctionMatchesFKFilter(item, deletedKey);
		});
	}

	function isNewlyUpdated(item: AnyItem): boolean {
		const key = getJunctionKey(item);

		return updateOps.value.some((op) => {
			const filter = op._update.filter as CompositePrimaryKey;
			if (key && isSameKey(filter, key)) return true;
			// Update filter uses FK fields, fall back to FK-based match
			return junctionMatchesFKFilter(item, filter);
		});
	}

	function getItemState(item: AnyItem): RelatedItemState {
		if (isNewlyCreated(item)) return 'created';
		if (isConnectedAsCreated(item)) return 'created';
		if (isNewlyConnected(item)) return 'connected';
		if (isNewlyDisconnected(item)) return 'disconnected';
		if (isNewlyUpdated(item)) return 'updated';
		return 'default';
	}

	// Autorefresh on submit
	if (formBlockContext) {
		formBlockContext.reportHasChanges(() => pendingTargetEdits.value.size > 0);

		formBlockContext.state.value?.$onSuccess?.(() => {
			remoteItems.refresh();
		});
	}

	return {
		items: displayItems,
		targetCollection,
		junctionCollection,
		m2mStructure,
		connectedTargetsFilter,
		pendingDeleteTargetKeys,
		connectTarget,
		disconnectTarget,
		isTargetPendingCreate,
		createTarget,
		undoCreateTarget,
		updateTarget,
		editConnectedTarget,
		captureTargetEdits,
		getJunctionKey,
		getTargetFromJunction,
		getTargetKeyFromJunction,
		getTargetReferenceKeyFromJunction,
		pageNumber,
		totalItemsCount,
		currentItemsCount,
		unpagedItemsCount,
		isLoading,
		isNewTargetCreate,
		isNewlyCreated,
		isNewlyConnected,
		isNewlyDisconnected,
		isNewlyUpdated,
		getItemState,
		junctionPrimaryKeyFields,
		targetReferenceKeyFields,
	};
}
